import { LANGUAGE_NAMES } from './client';

/**
 * System prompts for different translation scenarios
 */
export const SYSTEM_PROMPTS = {
  TRANSLATOR: `You are a professional translator specializing in UI/UX translations.
Your task is to translate JSON objects containing interface text while:
- Maintaining the exact same JSON structure and keys
- Translating only the values, keeping keys unchanged
- Preserving any technical terms, placeholders, or special formatting
- Ensuring translations are natural and contextually appropriate for the target language
- Keeping the same tone and formality level as the source
Return ONLY valid JSON without any markdown formatting or explanations.`,

  JSON_TRANSLATOR: `You are a professional translator. You will receive a JSON object where:
- Keys must remain EXACTLY the same
- Only values should be translated
- Preserve any HTML tags, markdown, or special formatting in the values
- Maintain placeholder patterns like __TAG_0__, __TAG_1__, etc.
Return ONLY a valid JSON object with the same structure.`,
} as const;

/**
 * Generate translation prompt for batch translation
 */
export const generateBatchTranslationPrompt = (
  sourceLang: string,
  targetLang: string,
  translations: Record<string, string>
): string => {
  const sourceLanguageName = LANGUAGE_NAMES[sourceLang] || sourceLang;
  const targetLanguageName = LANGUAGE_NAMES[targetLang] || targetLang;
  const jsonString = JSON.stringify(translations, null, 2);

  return `Translate all values in the following JSON object from ${sourceLanguageName} to ${targetLanguageName}.

IMPORTANT RULES:
1. Keep all keys EXACTLY the same
2. Translate ONLY the values
3. Preserve all placeholders like __TAG_0__, __TAG_1__, etc.
4. Preserve all HTML tags like <b>, <i>, <u>, etc.
5. Maintain the same JSON structure
6. Return ONLY valid JSON without markdown code blocks

JSON to translate:
${jsonString}`;
};

/**
 * Generate prompt for single text translation
 */
export const generateSingleTranslationPrompt = (
  sourceLang: string,
  targetLang: string,
  text: string
): string => {
  const sourceLanguageName = LANGUAGE_NAMES[sourceLang] || sourceLang;
  const targetLanguageName = LANGUAGE_NAMES[targetLang] || targetLang;

  return `Translate the following text from ${sourceLanguageName} to ${targetLanguageName}.
Preserve any HTML tags, placeholders, and special formatting.
Return ONLY the translated text without explanations.

Text to translate:
${text}`;
};
